import { useMutation } from '@provision/graphql-client';
import { useCallback, useState } from 'react';
import { differenceWith } from 'remeda';
import { useCurrentUserProfile } from 'src/graphql';
import {
  GET_NOTIFICATION_PREFERENCE_QUERY,
  MANAGE_NOTIFICATION_PREFERENCE_QUERY,
} from 'src/graphql/entities/notification-preference/notifications.operations';
import {
  NotificationPreferenceInputData,
  NotificationPreferenceResponse,
} from 'src/graphql/entities/notification-preference/notifications.types';
import { QueryVariables } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import { useGetNotificationEvents } from './use-get-notification-events';
import {
  GetNotificationPreferenceResponseQuery,
  useGetNotificationPreferenceQueryParams,
  useGetNotificationPreferences,
} from './use-get-notification-preference';
import { useGetNotificationRoles } from './use-get-notification-roles';
import { inputDataToNotificationPreference } from './utils';

export interface ManageNotificationPreferenceMutation {
  manageNotificationPreferences: { notificationPreferences: NotificationPreferenceResponse[] };
}

export type ManageNotificationPreferenceMutationParams = {
  data: {
    notificationPreferences: NotificationPreferenceInputData[];
    persistDefaults?: boolean;
  };
};

export const useManageNotificationPreference = () => {
  const { user } = useCurrentUserProfile();
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const { variables: getNotificationPreferenceQueryVariables } =
    useGetNotificationPreferenceQueryParams();
  const { notificationPreferences } = useGetNotificationPreferences();
  const { notificationEventsMap } = useGetNotificationEvents();
  const { notificationRolesMap } = useGetNotificationRoles();

  const [optimisticCache, setOptimisticCache] = useState<NotificationPreferenceResponse[]>([]);
  const [pendingReqCount, setPendingReqCount] = useState(0);

  const [manageNotificationPreferenceMutation] = useMutation<
    ManageNotificationPreferenceMutation,
    ManageNotificationPreferenceMutationParams
  >(MANAGE_NOTIFICATION_PREFERENCE_QUERY, {
    update(cache, { data }) {
      if (!data?.manageNotificationPreferences?.notificationPreferences.length) {
        return;
      }
      cache.updateQuery<GetNotificationPreferenceResponseQuery, QueryVariables>(
        {
          query: GET_NOTIFICATION_PREFERENCE_QUERY,
          variables: getNotificationPreferenceQueryVariables,
        },
        (cached) => {
          let localRecords = cached?.getNotificationPreference.records || [];
          // if there's another request was already dispatched then it's probably optimistically updated values.
          // we update current state with optimistic values as current cached data doesn't have information about
          // values that were optimisticly changed.
          if (pendingReqCount > 0) {
            localRecords = differenceWith(
              localRecords,
              optimisticCache,
              (a, b) => a.id === b.id,
            );
            localRecords.push(...optimisticCache);
          }

          const unchangedData = differenceWith(
            localRecords,
            data.manageNotificationPreferences.notificationPreferences,
            (a, b) => a.id === b.id,
          );

          return {
            getNotificationPreference: {
              __typename: 'NotificationPreferenceDataResponse',
              records: [
                ...unchangedData,
                ...data.manageNotificationPreferences.notificationPreferences,
              ],
            },
          };
        },
        { broadcastAll: true },
      );
    },
    optimisticResponse: (params) => {
      const nextNotificationPreferences =
        params.data.notificationPreferences.map<NotificationPreferenceResponse>((p) => {
          const existingPreference = notificationPreferences.find(
            (np) => np.id === p.notificationPreferenceId,
          );
          const notChangedUserNotificationRoles = differenceWith(
            existingPreference?.userNotificationRole ?? [],
            p.userNotificationRole ?? [],
            (a, b) => a.userNotificationRoleId === b.userNotificationRoleId,
          );
          const preferences = inputDataToNotificationPreference(
            p,
            { userId: user?.id!, partnerId: user?.partnerId! },
            { notificationEventsMap, notificationRolesMap },
          );
          preferences.userNotificationRole.push(...notChangedUserNotificationRoles);
          return preferences;
        });

      setOptimisticCache(nextNotificationPreferences);
      return {
        manageNotificationPreferences: {
          __typename: 'ManageNotificationPreferencesResponse',
          notificationPreferences: nextNotificationPreferences,
        },
      };
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to update notification preference');
      setPendingReqCount((state) => state - 1);
    },
    onCompleted: () => {
      showSuccessAlert(`Saved`, { autoHide: 1000 });
      setPendingReqCount((state) => state - 1);
    },
  });

  const manageNotificationPreference = useCallback(
    async (data: NotificationPreferenceInputData[]) => {
      setPendingReqCount((state) => state + 1);
      const result = await manageNotificationPreferenceMutation({
        variables: {
          data: {
            notificationPreferences: data,
          },
        },
        context: {
          omitIsolationModeHeader: true,
        },
      });
      return result.data?.manageNotificationPreferences.notificationPreferences ?? [];
    },
    [manageNotificationPreferenceMutation],
  );

  return {
    manageNotificationPreference,
  };
};
